import React, { useMemo } from 'react';

// import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import cs from 'classnames';
import { useFormikContext } from 'formik';
import isEmpty from 'lodash/isEmpty';
import { useRecoilValue } from 'recoil';

import formatAppointmentRecurrence from 'components/practice/NewAppointment/lib/formatAppointmentRecurrence';
import humanizeEvent from 'components/practice/appointment/EventForm/lib/humanizeEvent';
import AppointmentRecurrence from 'components/practice/appointment/NewAppointment/components/AppointmentRecurrence/AppointmentRecurrence';
import { currentPractice } from 'components/practice/practiceState';
import Button from 'components/shared/Buttons/Button';
import Icon from 'components/shared/Icon/Icon';
import Popover from 'components/shared/Popovers/Popover/Popover';

const RecurrenceHeader = ({ open, fromPopover, label, close }) => (
  <div
    className={`flex items-center justify-between gap-2 bg-white ${fromPopover && 'px-4 py-3 shadow-medium'}`}>
    <div className="flex items-center gap-2">
      <Icon icon="repeat-filled" size={20} color="primary" />
      <span className={`text-primary-${fromPopover ? '900' : open ? '700' : '500'} font-500`}>
        {label} recurrence
      </span>
    </div>
    {fromPopover && <Icon icon="new-close" color="neutral" onClick={close} />}
  </div>
);

const Recurrence = ({ className }) => {
  const formik = useFormikContext();
  const practice = useRecoilValue(currentPractice);
  const { values, setFieldValue } = formik;

  const handleRemoveRecurrence = (close) => {
    [
      'end_date',
      'repeat_event',
      'repeat_every',
      'ends_type',
      'repeat_on',
      'events',
      'weekdays'
    ].forEach((field) => setFieldValue(field, null));
    close();
  };

  const humanizedAppointment = useMemo(() => {
    if (!values.isRecurrence || !values.repeat_every) {
      return '';
    }

    const formattedRecurrence = formatAppointmentRecurrence(values);
    return humanizeEvent(
      {
        ...values,
        ...formattedRecurrence,
        weekdays:
          typeof formattedRecurrence.weekdays === 'string'
            ? JSON.parse(formattedRecurrence.weekdays)
            : formattedRecurrence.weekdays,
        start_date: values.starts_at
      },
      practice.timezone
    );
  }, [values, practice.timezone]);

  const isDeleteRecurrenceBtnDisabled =
    !values.isRecurrence &&
    [
      'end_date',
      'repeat_event',
      'repeat_every',
      'ends_type',
      'repeat_on',
      'events',
      'weekdays'
    ].every((field) => isEmpty(values[field]));

  return (
    <div className={cs(className, 'flex flex-col')}>
      <div className="flex items-center justify-between pb-2">
        <label className="m-0 text-sm font-500 text-neutral-800 first-letter:capitalize">
          Recurrence
        </label>
        {values.isRecurrence && (
          <Popover
            buttonClassName="border-none"
            icon="edit-2-mono"
            position="left"
            panelClassName="w-[500px] p-0"
            disabled={values.entity_changed}
            iconColor="primary">
            {({ closePopover }) => (
              <div>
                <RecurrenceHeader fromPopover={true} label="Manage" close={closePopover} />
                <div className="bg-primary-10 p-4">
                  <AppointmentRecurrence formik={formik} />
                </div>
                <div className="flex items-center justify-between px-4 py-3 shadow-medium">
                  <Button
                    text="Cancel"
                    onClick={closePopover}
                    color="neutral"
                    size="small"
                    outlined
                  />

                  <div className="flex items-center gap-2  ">
                    <Button
                      text="Remove recurrence"
                      onClick={() => handleRemoveRecurrence(closePopover)}
                      color="primary"
                      outlined
                      size="small"
                    />
                  </div>
                </div>
              </div>
            )}
          </Popover>
        )}
      </div>
      {values.isRecurrence ? (
        <label className="m-0 text-xs  text-primary-600 first-letter:capitalize">
          {humanizedAppointment}
        </label>
      ) : (
        <Popover
          icon="repeat-filled"
          position="right"
          panelClassName="w-[500px] p-0 overflow-y-auto"
          text="Add recurrence"
          buttonTheme="white">
          {({ closePopover }) => (
            <div>
              {/* <RecurrenceHeader open={true} label="Add" /> */}
              <RecurrenceHeader fromPopover={true} label="Add" />
              <div className="bg-primary-10 p-4">
                <AppointmentRecurrence formik={formik} />
              </div>
              <div className="flex items-center justify-between px-4 py-3 shadow-medium">
                <Button
                  text="Cancel"
                  onClick={closePopover}
                  color="neutral"
                  size="small"
                  outlined
                />

                <div className="flex items-center gap-2  ">
                  <Button
                    text="Remove recurrence"
                    onClick={() => handleRemoveRecurrence(closePopover)}
                    color="primary"
                    outlined
                    size="small"
                    disabled={isDeleteRecurrenceBtnDisabled}
                  />
                </div>
              </div>
            </div>
          )}
        </Popover>
      )}
    </div>
  );
};

export default Recurrence;
